import { CustomText, CustomTextStyles } from '@/components/CustomText';
import { Pressable, ScrollView, StyleSheet, View } from 'react-native';
import React, { useContext, useMemo, useRef, useState } from 'react';
import { AppText, AppTextStyles } from '@/components/AppText';
import { useRouter } from 'expo-router';
import { CommunityMembership } from "@/types/community_membership";
import * as AppUtils from '@/utilities/AppUtils';
import * as AppUtilities from '@/utilities/AppUtilities';
import * as AppConsts from '@/utilities/AppConsts';
import * as AppConstants from '@/utilities/AppConstants';
import { Image } from 'expo-image';
import { Panel } from 'react95-native-rabbl';
import { useWindowDimensions } from 'react-native';
import { useHeaderHeight } from "expo-router/react-navigation";
import { CustomDivider } from "@/components/CustomDivider";
import { Gallery, IGallery, GalleryItem } from "@/components/Gallery";
import { Link } from 'expo-router';
import { DisplayMarkdown } from "@/components/DisplayMarkdown";
import { ExternalLink } from '@/components/ExternalLink';

const styles = StyleSheet.create({
  container: {
    width: '100%',
    padding: 20,
    alignItems: 'center',
    backgroundColor: 'transparent',
    gap: 20,
    paddingTop: 0,
  },
  image: {
    width: AppConsts.BASIC_MINIMUM_WIDTH,
    height: AppConsts.BASIC_MINIMUM_WIDTH,
  },
  footer: {
    padding: 50,
  },
  editImageWrapper: {
    position: 'absolute',
    alignSelf: 'center',
    bottom: 0,
  },
  editImage: {
    width: 32,
    height: 32,
  },
  flexRowGapVerticalCenter: {
    flexDirection: 'row',
    gap: 10,
    alignItems: "center",
  },
  flexRowGap: {
    flexDirection: 'row',
    gap: 10,
    alignItems: "center",
  },
  flexRowGapTop: {
    flexDirection: 'row',
    gap: 10,
  },
  panel: {
    padding: 20,
    gap: 20,
    maxWidth: AppConsts.MAX_WIDTH_COMFORTABLE_READING,
  },
  headerItems: {
    gap: 5,
    alignItems: "center",
  },
  nameWrapper: {
    width: '100%',
    alignItems: "center",
    paddingBottom: 10,
  },
});

type Props = {
  member: CommunityMembership,
  bottomPadding?: boolean,
  edit?: boolean,
  showName?: boolean,
  extraOffsetHeight: number,
  referenceDomain?: string,
};

const Profile = ({
  member,
  bottomPadding = true,
  edit = false,
  showName = true,
  extraOffsetHeight = 0,
  referenceDomain,
}: Props) => {
  const router = useRouter();
  const {height : screenHeight, width : screenWidth} = useWindowDimensions();
  const headerHeight = useHeaderHeight();
  const maxImageWidth = screenWidth - 20;
  const maxImageHeight = screenHeight - AppConsts.PROFILE_DECISION_TOTAL_HEIGHT - headerHeight - extraOffsetHeight;
  const gallery = useRef<IGallery>(null);

  const visuals: GalleryItem[] = useMemo(() => {
    let result: GalleryItem[] = [];
    if (member) {
      result = member.profile.visuals.map((visual) => ({
        source: {
          uri: AppUtils.createBackendLink('/rabbl/api/visual?uniqueid=' + visual.uniqueid)
        },
        note: '',
        key: visual.uniqueid,
      }));
    }
    return result;
  }, [member]);

  return (
    <View style={styles.container}>
      {
        showName && (
          <View style={styles.flexRowGapVerticalCenter}>
            <CustomText>{member.profile.name}</CustomText>
            {
              edit && (
                <Link
                  href={{ pathname: '/settings', params: { edit: "y" } }} 
                  asChild={AppUtilities.isWeb()}
                >
                  <Image
                    style={styles.editImage}
                    source={require('@/assets/images/gears32x32.png')}
                  />
                </Link>
              )
            }
          </View>
        )
      }
      {
        visuals.map((visual, index) => (
          <View key={visual.key}>
            <Pressable
              onPress={() => gallery.current?.open(index)}
            >
              <Image
                alt={member.profile.name}
                style={{
                  width: maxImageWidth,
                  height: maxImageHeight,
                }}
                contentFit='contain'
                source={visual.source}
              />
            </Pressable>
          </View>
        ))
      }

      <Panel variant='raised' elevation={4}>
        <View style={styles.panel}>

          <View style={styles.headerItems}>
            <View style={styles.nameWrapper}><CustomText>{member.profile.name}</CustomText></View>

            <View style={styles.flexRowGapVerticalCenter}>
              <AppText>{AppUtils.getMemberAge(member)} - {AppUtils.getMemberShortLocation(member.user_info)}</AppText>
            </View>

            <View style={AppConstants.STYLES.alignCenter}>
              <AppText style={AppTextStyles.small}>
                {AppUtils.getDistanceDisplay(member.reference_distance_miles ?? 0)} miles
              </AppText>
            </View>

            {
              referenceDomain && referenceDomain != member.community?.domain &&
                <View style={AppConstants.STYLES.alignCenter}>
                  <AppText style={AppTextStyles.tinyItalics}>Collab with {member.community?.name}</AppText>
                </View>
            }
          </View>

          {
            (
              (member.profile.website && member.profile.website.length > 0) ||
              (member.profile.instagram && member.profile.instagram.length > 0) ||
              (member.profile.tiktok && member.profile.tiktok.length > 0) ||
              (member.profile.snapchat && member.profile.snapchat.length > 0) ||
              (member.profile.facebook && member.profile.facebook.length > 0) ||
              (member.profile.x && member.profile.x.length > 0)
            ) && (
              <View style={{ flexDirection: "row", gap: 20, alignSelf: "center", flexWrap: "wrap" }}>
                {
                  (member.profile.website && member.profile.website.length > 0) && (
                    <ExternalLink href={AppUtils.getWebsiteLink(member.profile.website)} target='_blank' className='nontext'>
                      <Image
                        style={AppConstants.STYLES.brandIcon}
                        source={require('@/assets/images/icons/externallink.png')}
                      />
                    </ExternalLink>
                  )
                }
                {
                  (member.profile.instagram && member.profile.instagram.length > 0) && (
                    <ExternalLink href={AppUtils.getInstagramLink(member.profile.instagram)} target='_blank' className='nontext'>
                      <Image
                        style={AppConstants.STYLES.brandIcon}
                        source={require('@/assets/images/brands/instagramicon.png')}
                      />
                    </ExternalLink>
                  )
                }
                {
                  (member.profile.tiktok && member.profile.tiktok.length > 0) && (
                    <ExternalLink href={AppUtils.getTikTokLink(member.profile.tiktok)} target='_blank' className='nontext'>
                      <Image
                        style={AppConstants.STYLES.brandIcon}
                        source={require('@/assets/images/brands/tiktokicon.png')}
                      />
                    </ExternalLink>
                  )
                }
                {
                  (member.profile.snapchat && member.profile.snapchat.length > 0) && (
                    <ExternalLink href={AppUtils.getSnapchatLink(member.profile.snapchat)} target='_blank' className='nontext'>
                      <Image
                        style={AppConstants.STYLES.brandIcon}
                        source={require('@/assets/images/brands/snapchaticon.png')}
                      />
                    </ExternalLink>
                  )
                }
                {
                  (member.profile.facebook && member.profile.facebook.length > 0) && (
                    <ExternalLink href={AppUtils.getFacebookLink(member.profile.facebook)} target='_blank' className='nontext'>
                      <Image
                        style={AppConstants.STYLES.brandIcon}
                        source={require('@/assets/images/brands/facebookicon.png')}
                      />
                    </ExternalLink>
                  )
                }
                {
                  (member.profile.x && member.profile.x.length > 0) && (
                    <ExternalLink href={AppUtils.getXLink(member.profile.x)} target='_blank' className='nontext'>
                      <Image
                        style={AppConstants.STYLES.brandIcon}
                        source={require('@/assets/images/brands/xicon.png')}
                      />
                    </ExternalLink>
                  )
                }
              </View>
            )
          }

          <CustomDivider fullWidth={true} />

          <View style={styles.flexRowGapTop}>
            
            {/*<AppText>{member.profile.description}</AppText>*/}

            <DisplayMarkdown markdown={member.profile.description} />

          </View>
        </View>
      </Panel>
      { bottomPadding && ( <View style={styles.footer} /> ) }
      <Gallery items={visuals} ref={gallery} />
    </View>
  );
};

export default Profile;
